/**
 * Copyright 2015 GeoSolutions Sas
 * Copyright Sourcepole AG
 * All rights reserved.
 *
 * This source code is licensed under the BSD-style license found in the
 * LICENSE file in the root directory of this source tree.
 */

import deepmerge from 'deepmerge';

import {getStore} from '../stores/StandardStore';
import ConfigUtils from './ConfigUtils';
import Request from './Request';


const LocaleUtils = {
    loadLocale(lang, fallbackLangData) {
        return new Promise(resolve => {
            let loadLang = null;
            let availableLanguages = ConfigUtils.getConfigProp("availableLocales");
            if (availableLanguages) {
                availableLanguages = Object.keys(availableLanguages);
            } else {
                availableLanguages = process.env.AvailableLanguages;
            }
            if (availableLanguages.indexOf(lang) !== -1) {
                // Exact match: lang-REGION
                loadLang = lang;
            } else if (availableLanguages.indexOf(lang.slice(0, 2)) !== -1) {
                // Exact match: lang
                loadLang = lang.slice(0, 2);
            } else {
                // Try match lang-<OTHER_REGION>
                loadLang = availableLanguages.find(lc => lc.slice(0, 2) === lang.slice(0, 2));
            }
            const config = {
                headers: {'Content-Type': 'application/json'},
                data: {}
            };
            const translationsPath = ConfigUtils.getTranslationsPath();
            const resolveLang = (locale, messages) => {
                if (ConfigUtils.getConfigProp("loadTranslationOverrides")) {
                    Request.get(translationsPath + '/' + locale + '_overrides.json', config).then(response => {
                        const overrideMessages = response.data.messages;
                        resolve({
                            locale: locale,
                            messages: deepmerge(messages, overrideMessages)
                        });
                    }).catch(() => {
                        resolve({
                            locale: locale,
                            messages: messages
                        });
                    });
                } else {
                    resolve({
                        locale: locale,
                        messages: messages
                    });
                }
            };
            if (!loadLang) {
                // eslint-disable-next-line
                console.warn("No suitable translations available for " + lang + ", defaulting to " + fallbackLangData.locale);
                resolveLang(fallbackLangData.locale, fallbackLangData.messages);
            } else {
                Request.get(translationsPath + '/' + loadLang + '.json', config).then(response => {
                    resolveLang(loadLang, response.data.messages);
                }).catch(() => {
                    // eslint-disable-next-line
                    console.warn("Failed to load translations for " + loadLang + ", defaulting to " + fallbackLangData.locale);
                    resolveLang(fallbackLangData.locale, fallbackLangData.messages);
                });
            }
        });
    },
    tr(key, ...args) {
        const state = getStore().getState();
        const text = key in state.locale.messages ? (state.locale.messages[key] || state.locale.fallbackMessages[key] || key) : key;

        if (args.length === 1 && args[0] && typeof args[0] === 'object' && !Array.isArray(args[0])) {
            // Named substitution: {name} replaced from the dictionary
            const dict = args[0];
            return text.replace(/{(\w+)}/g, (match, name) => (name in dict ? dict[name] : match));
        } else if (args.length > 0) {
            // Positional substitution: {0}, {1}, ...
            return text.replace(/{(\d+)}/g, (match, number) => {
                return typeof args[number] !== 'undefined' ? args[number] : match;
            });
        } else {
            return text;
        }
    },
    // Just a stub to make updateTranslations pick up the msgId
    trmsg(key) {
        return key;
    },
    trWithFallback(key, fallback) {
        const state = getStore().getState();
        return state.locale.messages[key] || fallback;
    },
    lang() {
        const state = getStore().getState();
        return state.locale.current;
    },
    toLocaleFixed(number, decimals) {
        if (ConfigUtils.getConfigProp("localeAwareNumbers")) {
            return number.toLocaleString(LocaleUtils.lang(), { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
        } else {
            return number.toFixed(decimals);
        }
    },
    commonTranslations() {
        return getStore().getState().locale.messagesTree.maptranslations || {};
    }
};

export default LocaleUtils;
